import { useEffect, useState } from 'react';

interface ProjectDetail {
  label: string;
  content: string;
}

interface Project {
  id: number;
  title: string;
  category: string;
  year: string;
  image: string;
  description?: string;
  location?: string;
  guests?: string;
  details?: ProjectDetail[];
  galleryImages?: string[];
}

interface ProjectModalProps {
  project: Project | null;
  onClose: () => void;
}

const ProjectModal = ({ project, onClose }: ProjectModalProps) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [project]);

  useEffect(() => {
    if (project) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [project]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (!project?.galleryImages) return;
      const total = project.galleryImages.length;
      if (e.key === 'ArrowRight') setActiveIndex((prev) => (prev + 1) % total);
      if (e.key === 'ArrowLeft') setActiveIndex((prev) => (prev - 1 + total) % total);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, project]);

  if (!project) return null;

  const images = project.galleryImages && project.galleryImages.length > 0
    ? project.galleryImages
    : [project.image];

  const total = images.length;

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev - 1 + total) % total);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev + 1) % total);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
      onClick={onClose}
    >
      {/* モバイル: 縦並び・100dvh固定 / PC: 横並び・720px/90vh */}
      <div
        className="relative bg-white w-full flex flex-col md:flex-row md:mx-8 md:max-w-5xl"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          @media (min-width: 768px) {
            .modal-inner {
              height: 720px !important;
              max-height: 90vh !important;
              overflow: hidden !important;
            }
            .modal-photo-area {
              height: 720px !important;
              max-height: 90vh !important;
            }
          }
          @media (max-width: 767px) {
            .modal-inner {
              height: 100dvh !important;
              max-height: 100dvh !important;
              overflow: hidden !important;
            }
            .modal-photo-area {
              height: 55dvh !important;
              max-height: 55dvh !important;
            }
          }
        `}</style>
        <div className="modal-inner relative bg-white w-full flex flex-col md:flex-row">
          {/* 閉じるボタン */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 md:top-4 md:right-4 z-20 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center text-gray-600 hover:opacity-70 transition-opacity cursor-pointer bg-gray-100"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          {/* 写真エリア: モバイル=55dvh / PC=50%幅・720px全高 */}
          <div className="modal-photo-area w-full md:w-1/2 flex-shrink-0 flex flex-col">
            {/* メイン画像 */}
            <div className="relative overflow-hidden bg-black flex-1" style={{ minHeight: 0 }}>
              {images.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`${project.title} ${i + 1}`}
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                  style={{
                    opacity: i === activeIndex ? 1 : 0,
                    objectPosition: project.id === 6
                      ? 'center center'
                      : 'center 20%',
                  }}
                />
              ))}

              {total > 1 && (
                <>
                  <button
                    onClick={handlePrev}
                    className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 z-10 w-8 md:w-9 h-8 md:h-9 flex items-center justify-center text-white hover:opacity-70 transition-opacity cursor-pointer"
                    style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
                  >
                    <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
                  </button>
                  <button
                    onClick={handleNext}
                    className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 z-10 w-8 md:w-9 h-8 md:h-9 flex items-center justify-center text-white hover:opacity-70 transition-opacity cursor-pointer"
                    style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
                  >
                    <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
                  </button>
                  <div className="absolute bottom-2 md:bottom-4 left-1/2 -translate-x-1/2 flex items-center space-x-1.5 md:space-x-2 z-10">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        onClick={(e) => { e.stopPropagation(); setActiveIndex(i); }}
                        className="cursor-pointer transition-all duration-300"
                        style={{
                          width: i === activeIndex ? '16px' : '5px',
                          height: '5px',
                          borderRadius: '3px',
                          backgroundColor: i === activeIndex ? '#fff' : 'rgba(255,255,255,0.5)',
                        }}
                      />
                    ))}
                  </div>
                  <div
                    className="absolute top-3 left-3 md:top-4 md:left-4 z-10 text-white text-xs tracking-widest px-2 md:px-3 py-0.5 md:py-1"
                    style={{ backgroundColor: 'rgba(0,0,0,0.4)', fontFamily: 'sans-serif' }}
                  >
                    {activeIndex + 1} / {total}
                  </div>
                </>
              )}
            </div>

            {/* サムネイル */}
            {total > 1 && (
              <div
                className="flex gap-1.5 md:gap-2 px-3 md:px-4 py-2 md:py-3 overflow-x-auto bg-white border-t border-gray-100"
                style={{ flexShrink: 0, height: '52px', minHeight: '52px' }}
              >
                {images.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIndex(i)}
                    className="flex-shrink-0 cursor-pointer transition-all duration-300"
                    style={{
                      width: '52px',
                      height: '36px',
                      border: i === activeIndex ? '2px solid #000' : '2px solid transparent',
                      opacity: i === activeIndex ? 1 : 0.55,
                    }}
                  >
                    <img src={src} alt={`thumb-${i}`} className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 説明エリア */}
          <div
            className="w-full md:w-1/2 overflow-y-auto flex flex-col"
            style={{ flex: 1, minHeight: 0 }}
          >
            <div className="p-5 md:p-10 flex flex-col h-full">
              {/* Meta */}
              <div className="flex items-center space-x-3 mb-3 md:mb-6">
                <div className="w-8 md:w-12 h-px bg-black"></div>
                <span className="text-xs tracking-[0.4em] text-gray-500 uppercase font-light">
                  {project.category}
                </span>
              </div>

              {/* Title */}
              <h2 className="text-lg md:text-3xl font-extralight text-black tracking-tight leading-tight mb-3 md:mb-4">
                {project.title}
              </h2>

              {/* Location & guests */}
              {(project.location || project.guests) && (
                <div className="flex flex-wrap gap-3 mb-3 md:mb-8 text-xs md:text-sm text-gray-500 font-light">
                  {project.location && (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-line text-gray-400"></i>
                      </div>
                      <span>{project.location}</span>
                    </div>
                  )}
                  {project.guests && (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-group-line text-gray-400"></i>
                      </div>
                      <span>{project.guests}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Description */}
              {project.description && (
                <p className="text-xs md:text-base font-light text-gray-600 leading-relaxed mb-3 md:mb-8">
                  {project.description}
                </p>
              )}

              {/* Detail items */}
              {project.details && project.details.length > 0 && (
                <div className="border-t border-gray-100 pt-3 md:pt-8">
                  <div className="grid grid-cols-1 gap-2 md:gap-6">
                    {project.details.map((detail, i) => (
                      <div key={i} className="flex flex-col space-y-1 md:space-y-2">
                        <div className="flex items-center space-x-3">
                          <div className="w-4 md:w-6 h-px bg-black"></div>
                          <span className="text-xs tracking-[0.3em] text-black uppercase font-light">
                            {detail.label}
                          </span>
                        </div>
                        <p className="text-xs md:text-sm font-light text-gray-600 leading-relaxed pl-7 md:pl-9">
                          {detail.content}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="mt-auto pt-3 md:pt-8 border-t border-gray-100">
                <button
                  onClick={() => {
                    onClose();
                    setTimeout(() => {
                      const el = document.getElementById('contact');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }, 300);
                  }}
                  className="inline-flex items-center space-x-3 text-xs md:text-sm tracking-[0.3em] uppercase font-light text-black hover:opacity-60 transition-opacity cursor-pointer whitespace-nowrap"
                >
                  <span>お問い合わせ</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
